import {
	COLOUR_GRAPEFRUIT,
	COLOUR_CITRON,
	COLOUR_TEAL,
	hexToRGBAstring,
} from "../../../js";

// Categories for organising permissions
export const PERMISSION_CATEGORIES = {
	USER_MANAGEMENT: "User Management",
	EVENTS: "Events",
	COMMUNICATION: "Communication",
	FACILITIES_BOOKINGS: "Facilities & Bookings",
	SITE_ADMINISTRATION: "Site Administration",
	PAYMENTS: "Payments",
	CONTENT: "Content Management",
	EXTRAS: "Extras",
	PROFILE_SETTINGS: "Profile Settings",
	USER_FEATURES: "User Features",
};

// NOTE: important and optional are currently hidden
export const IMPORTANCE_LEVELS = {
	CRITICAL: "critical",
	IMPORTANT: "important",
	OPTIONAL: "optional",
};

/**
 * Description of the built-in permissions.
 */
export const PERMISSIONS_CONFIG = [
	// User Management
	{
		displayName: "Manage User Types",
		key: "permissionManagement",
		category: PERMISSION_CATEGORIES.USER_MANAGEMENT,
		helperText:
			"Allows creating user types and editing the permissions associated with user types.",
		importance: IMPORTANCE_LEVELS.CRITICAL,
	},
	{
		displayName: "Manage Users",
		key: "userManagement",
		category: PERMISSION_CATEGORIES.USER_MANAGEMENT,
		helperText: "Allows adding new people or changing their details.",
		importance: IMPORTANCE_LEVELS.CRITICAL,
	},
	{
		displayName: "Disable Users",
		key: "disableUser",
		category: PERMISSION_CATEGORIES.USER_MANAGEMENT,
		helperText: "Allows blocking a person so they cannot log in.",
		importance: IMPORTANCE_LEVELS.IMPORTANT,
	},
	{
		displayName: "Manage User Tags",
		key: "userTagManagement",
		category: PERMISSION_CATEGORIES.USER_MANAGEMENT,
		helperText: "Allows setting up new tags to sort people into.",
		importance: IMPORTANCE_LEVELS.OPTIONAL,
	},
	{
		displayName: "Assign User Tags",
		key: "userTagAssignment",
		category: PERMISSION_CATEGORIES.USER_MANAGEMENT,
		helperText: "Allows assigning a tag on a person's profile.",
		importance: IMPORTANCE_LEVELS.OPTIONAL,
	},
	{
		displayName: "Manage Contacts",
		key: "importantContacts",
		category: PERMISSION_CATEGORIES.USER_MANAGEMENT,
		helperText: "Allows adding, editing, and removing important contacts.",
		importance: IMPORTANCE_LEVELS.IMPORTANT,
	},

	// Events
	{
		displayName: "Manage Events",
		key: "events",
		category: PERMISSION_CATEGORIES.EVENTS,
		helperText:
			"Allows creating, editing and deleting events that appear in the app.",
		importance: IMPORTANCE_LEVELS.IMPORTANT,
	},
	{
		displayName: "Submit Events",
		key: "eventSubmit",
		category: PERMISSION_CATEGORIES.EVENTS,
		helperText: "Allows submitting an event that an admin must approve first.",
		importance: IMPORTANCE_LEVELS.IMPORTANT,
	},
	{
		displayName: "Track Event Attendance",
		key: "eventAttendance",
		category: PERMISSION_CATEGORIES.EVENTS,
		helperText:
			"Allows seeing who is coming to an event and booking other people into an event.",
		importance: IMPORTANCE_LEVELS.IMPORTANT,
	},
	{
		displayName: "Process Event Payments",
		key: "eventPayment",
		category: PERMISSION_CATEGORIES.EVENTS,
		helperText: "Allows taking money for event tickets.",
		importance: IMPORTANCE_LEVELS.IMPORTANT,
	},

	// Communication
	{
		displayName: "Send Alerts to Users",
		key: "alerts",
		category: PERMISSION_CATEGORIES.COMMUNICATION,
		helperText: "Allows sending alerts and notifications to users.",
		importance: IMPORTANCE_LEVELS.IMPORTANT,
	},
	{
		displayName: "Manage Comments",
		key: "comments",
		category: PERMISSION_CATEGORIES.COMMUNICATION,
		helperText:
			"Allows viewing and moderating all comments, including on content with restricted visibility.",
		importance: IMPORTANCE_LEVELS.IMPORTANT,
	},

	// Facilities & Bookings
	{
		displayName: "Manage Bookings",
		key: "bookings",
		category: PERMISSION_CATEGORIES.FACILITIES_BOOKINGS,
		helperText:
			"Allows full booking administration including managing calendars, booking types, and bookings for any room.",
		importance: IMPORTANCE_LEVELS.IMPORTANT,
	},
	{
		displayName: "Manage Calendar Bookings",
		key: "assignedBookings",
		category: PERMISSION_CATEGORIES.FACILITIES_BOOKINGS,
		helperText:
			"Allows changing bookings only for calendars you have been assigned as admin for.",
		importance: IMPORTANCE_LEVELS.IMPORTANT,
	},
	{
		displayName: "Manage Facilities",
		key: "facilities",
		category: PERMISSION_CATEGORIES.FACILITIES_BOOKINGS,
		helperText:
			"Allows creating, editing and deleting facilities that appear in the app.",
		importance: IMPORTANCE_LEVELS.IMPORTANT,
	},
	{
		displayName: "Manage Sign Ins",
		key: "visitors",
		category: PERMISSION_CATEGORIES.FACILITIES_BOOKINGS,
		helperText:
			"Allows managing contractor records, sign-in logs, and form lockout events.",
		importance: IMPORTANCE_LEVELS.IMPORTANT,
	},
	{
		displayName: "Manage Keys and Cards",
		key: "keysCards",
		category: PERMISSION_CATEGORIES.FACILITIES_BOOKINGS,
		helperText: "Allows giving keys or swipe cards to people.",
		importance: IMPORTANCE_LEVELS.IMPORTANT,
	},
	{
		displayName: "Manage Maps",
		key: "maps",
		category: PERMISSION_CATEGORIES.FACILITIES_BOOKINGS,
		helperText: "Allows adding maps to help people find their way.",
		importance: IMPORTANCE_LEVELS.OPTIONAL,
	},

	// Site Administration
	{
		displayName: "Manage App Terms",
		key: "terms",
		category: PERMISSION_CATEGORIES.SITE_ADMINISTRATION,
		helperText:
			"Allows changing the terms and conditions people must agree to.",
		importance: IMPORTANCE_LEVELS.IMPORTANT,
	},
	{
		displayName: "Feature Picker",
		key: "featurePicker",
		category: PERMISSION_CATEGORIES.SITE_ADMINISTRATION,
		helperText: "Allows choosing which features appear in the app.",
		importance: IMPORTANCE_LEVELS.CRITICAL,
	},
	{
		displayName: "Activate Devices",
		key: "activateDevices",
		category: PERMISSION_CATEGORIES.SITE_ADMINISTRATION,
		helperText: "Allows activating the TV app.",
		importance: IMPORTANCE_LEVELS.IMPORTANT,
	},
	{
		displayName: "Share Invite Codes",
		key: "inviteCode",
		category: PERMISSION_CATEGORIES.SITE_ADMINISTRATION,
		helperText: "Allows making codes to help new people join.",
		importance: IMPORTANCE_LEVELS.IMPORTANT,
	},

	// Content Management
	{
		displayName: "Manage Information Pages",
		key: "infoPages",
		category: PERMISSION_CATEGORIES.CONTENT,
		helperText: "Allows writing help pages and answers to questions.",
		importance: IMPORTANCE_LEVELS.OPTIONAL,
	},
	{
		displayName: "Create and View Surveys",
		key: "polls",
		category: PERMISSION_CATEGORIES.CONTENT,
		helperText:
			"Allows asking questions to the community to see what they think.",
		importance: IMPORTANCE_LEVELS.IMPORTANT,
	},
	{
		displayName: "Submit Surveys for Users",
		key: "pollsSubmit",
		category: PERMISSION_CATEGORIES.CONTENT,
		helperText: "Allows submitting survey responses on behalf of other people.",
		importance: IMPORTANCE_LEVELS.OPTIONAL,
	},
	{
		displayName: "Manage Offers",
		key: "offers",
		category: PERMISSION_CATEGORIES.CONTENT,
		helperText:
			"Allows creating, editing and deleting offers that appear in the app.",
		importance: IMPORTANCE_LEVELS.OPTIONAL,
	},
	{
		displayName: "Manage Photo Library",
		key: "manageImageLibrary",
		category: PERMISSION_CATEGORIES.CONTENT,
		helperText:
			"Allows full management of photo folders and their contents, including both private and public libraries.",
		importance: IMPORTANCE_LEVELS.OPTIONAL,
	},
	{
		displayName: "Add to Photo Folders",
		key: "addToImageLibrary",
		category: PERMISSION_CATEGORIES.CONTENT,
		helperText: "Allows uploading photos to a folder.",
		importance: IMPORTANCE_LEVELS.OPTIONAL,
	},
	{
		displayName: "Add to Gallery",
		key: "addToPublishingImageLibrary",
		category: PERMISSION_CATEGORIES.CONTENT,
		helperText:
			"Allows uploading photos that appear in the gallery in the app.",
		importance: IMPORTANCE_LEVELS.OPTIONAL,
	},
	{
		displayName: "Manage Restaurant",
		key: "food",
		category: PERMISSION_CATEGORIES.CONTENT,
		helperText: "Allows changing food menu and prices.",
		importance: IMPORTANCE_LEVELS.OPTIONAL,
	},
	{
		displayName: "Manage Forms",
		key: "forms",
		category: PERMISSION_CATEGORIES.CONTENT,
		helperText: "Allows making forms to collect information.",
		importance: IMPORTANCE_LEVELS.IMPORTANT,
	},
	{
		displayName: "Manage Services",
		key: "services",
		category: PERMISSION_CATEGORIES.CONTENT,
		helperText: "Allows creating and managing services and their categories.",
		importance: IMPORTANCE_LEVELS.OPTIONAL,
	},
	{
		displayName: "Manage Feedback",
		key: "feedback",
		category: PERMISSION_CATEGORIES.CONTENT,
		helperText:
			"Allows configuring feedback settings and deleting feedback entries.",
		importance: IMPORTANCE_LEVELS.OPTIONAL,
	},

	// Payments
	{
		displayName: "Manage Payments",
		key: "managePayments",
		category: PERMISSION_CATEGORIES.PAYMENTS,
		helperText: "Allows full control over all payments.",
		importance: IMPORTANCE_LEVELS.CRITICAL,
	},
	{
		displayName: "Refund Payments",
		key: "refundPayments",
		category: PERMISSION_CATEGORIES.PAYMENTS,
		helperText: "Allows refunding a payment.",
		importance: IMPORTANCE_LEVELS.IMPORTANT,
	},
	{
		displayName: "View Payments",
		key: "viewPayments",
		category: PERMISSION_CATEGORIES.PAYMENTS,
		helperText:
			"Allows looking at payments made by the community without changing anything.",
		importance: IMPORTANCE_LEVELS.IMPORTANT,
	},
];

/**
 * User features permissions (formerly Hidden Sections)
 */
export const USER_FEATURES_CONFIG = [
	{
		displayName: "Allow viewing My Events",
		key: "myEvents",
		helperText: "Users can see events they've registered for",
		importance: IMPORTANCE_LEVELS.IMPORTANT,
		category: PERMISSION_CATEGORIES.EVENTS,
	},
	{
		displayName: "Allow viewing Contacts",
		key: "importantContacts",
		helperText: "Users can access contacts directory",
		importance: IMPORTANCE_LEVELS.IMPORTANT,
		category: PERMISSION_CATEGORIES.USER_MANAGEMENT,
	},
	// {
	// 	displayName: "People Page",
	// 	key: "people",
	// 	helperText: "Users can view the People directory",
	// 	importance: IMPORTANCE_LEVELS.IMPORTANT,
	// 	category: PERMISSION_CATEGORIES.USER_MANAGEMENT,
	// },
	{
		displayName: "Allow viewing Maps",
		key: "maps",
		helperText: "Users can view site maps and directions",
		importance: IMPORTANCE_LEVELS.OPTIONAL,
		category: PERMISSION_CATEGORIES.FACILITIES_BOOKINGS,
	},
	{
		displayName: "Allow registering for Events",
		key: "eventRegistration",
		helperText: "Users can sign up for community events",
		importance: IMPORTANCE_LEVELS.IMPORTANT,
		category: PERMISSION_CATEGORIES.EVENTS,
	},
	{
		displayName: "Allow calling other users",
		key: "phonePeople",
		helperText: "Users can call others from People list",
		importance: IMPORTANCE_LEVELS.OPTIONAL,
		category: PERMISSION_CATEGORIES.USER_MANAGEMENT,
	},
	{
		displayName: "Allow viewing comments",
		key: "viewComment",
		helperText: "Users can read comments on posts and pages",
		importance: IMPORTANCE_LEVELS.IMPORTANT,
		category: PERMISSION_CATEGORIES.COMMUNICATION,
	},
	{
		displayName: "Allow posting comments",
		key: "addComment",
		helperText: "Users can write and submit comments",
		importance: IMPORTANCE_LEVELS.IMPORTANT,
		category: PERMISSION_CATEGORIES.COMMUNICATION,
	},
	{
		displayName: "Allow seeing announcements",
		key: "adminBanner",
		helperText: "Users can view community manager announcements",
		importance: IMPORTANCE_LEVELS.IMPORTANT,
		category: PERMISSION_CATEGORIES.CONTENT,
	},
	{
		displayName: "Allow seeing community news",
		key: "adminBlog",
		helperText: "Users can view community insights and news",
		importance: IMPORTANCE_LEVELS.IMPORTANT,
		category: PERMISSION_CATEGORIES.CONTENT,
	},
	// {
	// 	displayName: "Hub",
	// 	key: "marketplace",
	// 	helperText: "Users can access the Hub",
	// 	importance: IMPORTANCE_LEVELS.IMPORTANT,
	// 	category: PERMISSION_CATEGORIES.CONTENT,
	// },
];

// Additional settings configuration
export const SETTINGS_CONFIG = [
	{
		key: "profileBirthday",
		text: "Allow adding a birthday from their profile",
		importance: IMPORTANCE_LEVELS.IMPORTANT,
		category: PERMISSION_CATEGORIES.PROFILE_SETTINGS,
	},
	{
		key: "profileECs",
		text: "Allow adding Emergency Contacts from their profile",
		importance: IMPORTANCE_LEVELS.IMPORTANT,
		category: PERMISSION_CATEGORIES.PROFILE_SETTINGS,
	},
];

/**
 * Helper function to get permissions by category
 *
 * @param {Array<{ category: string }>} permissions - List of permissions.
 * @returns {{ [category: string]: Array<{ category: string }> }} Grouped permissions by category.
 */
export const getPermissionsByCategory = (permissions) => {
	const grouped = {};
	Object.values(PERMISSION_CATEGORIES).forEach((category) => {
		grouped[category] = permissions.filter((p) => p.category === category);
	});
	return grouped;
};

// Helper function to get all configurable items (permissions, user features, settings) by category
// Returns an object with category names as keys and arrays of items as values
export const getAllConfigurableItemsByCategory = (
	permissions,
	userFeatures,
	settings,
) => {
	const grouped = {};

	Object.values(PERMISSION_CATEGORIES).forEach((category) => {
		grouped[category] = [];
	});

	permissions.forEach((p) => {
		if (p.category && grouped[p.category]) {
			grouped[p.category].push({ ...p, type: "permission" });
		}
	});

	userFeatures.forEach((f) => {
		if (f.category && grouped[f.category]) {
			grouped[f.category].push({ ...f, type: "userFeature" });
		}
	});

	settings.forEach((s) => {
		if (s.category && grouped[s.category]) {
			grouped[s.category].push({ ...s, displayName: s.text, type: "setting" });
		}
	});

	return grouped;
};

/**
 * Check if a permission is critical
 *
 * @param {string} permissionKey
 * @returns {boolean}
 */
export const isCriticalPermission = (permissionKey) => {
	const permission = PERMISSIONS_CONFIG.find((p) => p.key === permissionKey);
	return permission && permission.importance === IMPORTANCE_LEVELS.CRITICAL;
};

/**
 * Helper function to get styling details for each importance badge.
 *
 * @param {string} importance - The importance level (e.g., 'CRITICAL', 'IMPORTANT', 'OPTIONAL').
 * @returns {{
 *   label: string,
 *   color: string,
 *   borderColor: string,
 *   backgroundColor: string,
 *   textColor: string
 * }} Styling info for the importance badge, defaults to OPTIONAL.
 */
export const getImportanceInfo = (importance) => {
	const info = {
		[IMPORTANCE_LEVELS.CRITICAL]: {
			label: "Critical",
			color: COLOUR_GRAPEFRUIT,
			borderColor: COLOUR_GRAPEFRUIT,
			backgroundColor: "transparent",
			textColor: COLOUR_GRAPEFRUIT,
		},
		[IMPORTANCE_LEVELS.IMPORTANT]: {
			label: "Important",
			color: COLOUR_CITRON,
			borderColor: COLOUR_CITRON,
			backgroundColor: "transparent",
			textColor: hexToRGBAstring(COLOUR_CITRON, 0.75),
		},
		[IMPORTANCE_LEVELS.OPTIONAL]: {
			label: "Optional",
			color: COLOUR_TEAL,
			borderColor: COLOUR_TEAL,
			backgroundColor: "transparent",
			textColor: hexToRGBAstring(COLOUR_TEAL, 0.75),
		},
	};
	return info[importance] || info[IMPORTANCE_LEVELS.OPTIONAL];
};
